import { useCallback, useEffect, useRef } from 'react';

import { getEmergencyAutoConnectDecision } from './emergencyAutoConnectPolicy';

import { consoleLogDev } from 'common/helpers/consoleLogDev';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { updateCalleePhoneNumber } from 'features/call/call-base/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { setPrimaryHearingPhoneNumber } from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { EmergencyConnectionPoint } from 'features/emergency/enums';
import {
  addCalledConnectionPoints,
  emergencyConnectionPointSelector,
  emergencyPhoneNumberSelector,
  isAddressVerificationNeededSelector,
  markAddressVerified,
  phoneNumberPSAPSelector,
  selectIsAbandonedEmergencyCall,
  selectIsEmergencyAutoConnectBlockedByTeamingSync,
  selectIsLoggedOutEmergencyCall,
  setEmergencyConnectionPoint,
  setIsEmergencyDeafReconnectionNeeded,
  turnOffSpawningEmergencyCall,
} from 'features/emergency/store';
import { selectIsEmergencyHearingNotDisconnected } from 'features/emergency/store/emergencyHearingSelectors';
import { isAcceptingUserSelector } from 'features/teaming/teaming-base/store';
import { useWhileComponentMounted } from 'features/utils/hook';
import { useHearingConnect } from 'features/voice-meeting/hooks';
import { psapFallbackOnDeafDisconnectAnalytics } from 'features/emergency/helpers';

const noEmergencyConnectionPointAttemptKey =
  '__NO_EMERGENCY_CONNECTION_POINT__';

const getAutoConnectAttemptKey = (
  emergencyConnectionPoint?: EmergencyConnectionPoint
) => emergencyConnectionPoint ?? noEmergencyConnectionPointAttemptKey;

export const useEmergencyAutoConnect = () => {
  const whileComponentMounted = useWhileComponentMounted();
  const dispatch = useAppDispatch();
  const { connectHearing } = useHearingConnect();
  const emergencyPhoneNumber = useAppSelector(emergencyPhoneNumberSelector);
  const isAddressVerificationNeeded = useAppSelector(
    isAddressVerificationNeededSelector
  );
  const isAbandonedEmergencyCall = useAppSelector(
    selectIsAbandonedEmergencyCall
  );
  const emergencyConnectionPoint = useAppSelector(
    emergencyConnectionPointSelector
  );
  const isLoggedOutEmergencyCall = useAppSelector(
    selectIsLoggedOutEmergencyCall
  );
  const callId = useAppSelector(callIdSelector);
  const isAcceptingUser = useAppSelector(isAcceptingUserSelector);
  const isEmergencyAutoConnectBlockedByTeamingSync = useAppSelector(
    selectIsEmergencyAutoConnectBlockedByTeamingSync
  );
  const isEmergencyHearingNotDisconnected = useAppSelector(
    selectIsEmergencyHearingNotDisconnected
  );
  const phoneNumberPSAP = useAppSelector(phoneNumberPSAPSelector);
  const attemptedAutoConnectKeyRef = useRef<string | null>(null);
  const lastAutoConnectLogMessageRef = useRef<string | null>(null);
  const currentAutoConnectAttemptKey = getAutoConnectAttemptKey(
    emergencyConnectionPoint
  );

  const hasAttemptedAutoConnect = useCallback(
    () => attemptedAutoConnectKeyRef.current === currentAutoConnectAttemptKey,
    [currentAutoConnectAttemptKey]
  );

  const markAutoConnectAttempted = useCallback(() => {
    attemptedAutoConnectKeyRef.current = currentAutoConnectAttemptKey;
  }, [currentAutoConnectAttemptKey]);

  const attemptAutoConnect = useCallback(async () => {
    try {
      await dispatch(updateCalleePhoneNumber()).unwrap();
      dispatch(setPrimaryHearingPhoneNumber(emergencyPhoneNumber));
      await connectHearing(emergencyPhoneNumber);
      if (emergencyConnectionPoint) {
        dispatch(addCalledConnectionPoints(emergencyConnectionPoint));
      }
      dispatch(setIsEmergencyDeafReconnectionNeeded(false));
    } finally {
      dispatch(turnOffSpawningEmergencyCall());
    }
  }, [
    connectHearing,
    emergencyConnectionPoint,
    emergencyPhoneNumber,
    dispatch,
  ]);

  const handleDeafUnavailableStatusChange = useCallback(() => {
    dispatch(setPrimaryHearingPhoneNumber(phoneNumberPSAP));
    dispatch(setEmergencyConnectionPoint(EmergencyConnectionPoint.PSAP));
    dispatch(markAddressVerified());
  }, [dispatch, phoneNumberPSAP]);

  const triggerPSAPFallbackOnDeafDisconnect = useCallback(() => {
    const isAutoConnectAttempted = hasAttemptedAutoConnect();

    if (
      isLoggedOutEmergencyCall &&
      isAddressVerificationNeeded &&
      !isAutoConnectAttempted
    ) {
      handleDeafUnavailableStatusChange();
      dispatch(
        psapFallbackOnDeafDisconnectAnalytics({
          isLoggedOutEmergencyCall,
          isAddressVerificationNeeded,
          isAutoConnectAttempted,
        })
      );
    }
  }, [
    dispatch,
    hasAttemptedAutoConnect,
    handleDeafUnavailableStatusChange,
    isAddressVerificationNeeded,
    isLoggedOutEmergencyCall,
  ]);

  useEffect(() => {
    if (
      isLoggedOutEmergencyCall &&
      isAbandonedEmergencyCall &&
      isAddressVerificationNeeded
    ) {
      handleDeafUnavailableStatusChange();
    }
  }, [
    handleDeafUnavailableStatusChange,
    isAbandonedEmergencyCall,
    isAddressVerificationNeeded,
    isLoggedOutEmergencyCall,
  ]);

  const logDev = (message: string) => {
    if (lastAutoConnectLogMessageRef.current === message) {
      return;
    }

    lastAutoConnectLogMessageRef.current = message;
    consoleLogDev(`DEBUG: Emergency auto connect. ${message}.`);
  };

  useEffect(() => {
    const isAutoConnectAttempted = hasAttemptedAutoConnect();

    const autoConnectDecision = getEmergencyAutoConnectDecision({
      callId,
      emergencyConnectionPoint,
      emergencyPhoneNumber,
      isAcceptingUser,
      isAddressVerificationNeeded,
      isAutoConnectAttempted,
      isEmergencyAutoConnectBlockedByTeamingSync,
      isEmergencyHearingNotDisconnected,
      isLoggedOutEmergencyCall,
    });

    logDev(autoConnectDecision.logMessage);

    if (!autoConnectDecision.shouldAttemptAutoConnect) {
      // Clear the in-progress spawn flag so emergency UI does not stay locked
      // as if a new emergency call is still being created.
      if (autoConnectDecision.shouldTurnOffSpawningCall) {
        dispatch(turnOffSpawningEmergencyCall());
      }

      if (autoConnectDecision.shouldMarkAutoConnectAttempted) {
        markAutoConnectAttempted();
      }

      return;
    }

    markAutoConnectAttempted();
    void attemptAutoConnect();
  }, [
    isEmergencyHearingNotDisconnected,
    callId,
    isAddressVerificationNeeded,
    isAcceptingUser,
    isEmergencyAutoConnectBlockedByTeamingSync,
    hasAttemptedAutoConnect,
    markAutoConnectAttempted,
    attemptAutoConnect,
    emergencyConnectionPoint,
    emergencyPhoneNumber,
    isLoggedOutEmergencyCall,
    dispatch,
  ]);

  useEffect(() => {
    const deafUnavailableSubscription =
      ParticipantsStatusEventBus.deaf.$unavailable
        .pipe(whileComponentMounted())
        .subscribe(handleDeafUnavailableStatusChange);

    const deafDisconnectedSubscription =
      ParticipantsStatusEventBus.deaf.$disconnected
        .pipe(whileComponentMounted())
        .subscribe(triggerPSAPFallbackOnDeafDisconnect);

    return () => {
      deafUnavailableSubscription.unsubscribe();
      deafDisconnectedSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDeafUnavailableStatusChange, triggerPSAPFallbackOnDeafDisconnect]);
};
